import type { ComponentType } from 'react'
import {
  IconeAfastar,
  IconeAproximar,
  IconeDesfazer,
  IconeEnquadrarSelecao,
  IconeRefazer,
  IconeRestaurarCamera,
  IconeVistaFrontal,
  IconeVistaIsometrica,
  IconeVistaLateral,
  IconeVistaSuperior,
  type PropsIcone,
} from '../icones'
import type { TipoComandoCamera, VistaNomeada } from '../../visualizacao3d/camera/comandosCamera'
import type { ModoLayout } from './useModoLayout'

export interface Ferramenta {
  id: string
  rotulo: string
  Icone: ComponentType<PropsIcone>
  aoAcionar: () => void
  /** Marca o botão como estado atual (`aria-pressed`), não como ação. */
  ativa?: boolean
  /** Quando presente, o botão fica desabilitado e isto explica por quê. */
  motivoIndisponivel?: string
  /** Tecla que aciona a ferramenta. Entra na dica, nunca no nome acessível. */
  atalho?: string
}

interface Props {
  camera: {
    executar: (tipo: TipoComandoCamera) => void
    vistaAtiva: VistaNomeada | null
  }
  /** Só há o que enquadrar quando algo está selecionado. */
  temSelecao: boolean
  historico?: {
    desfazer: () => void
    refazer: () => void
    podeDesfazer: boolean
    podeRefazer: boolean
  }
  /** Explica a ausência de histórico no módulo atual. */
  motivoSemHistorico?: string
  modoLayout: ModoLayout
  /** Grupos adicionais, específicos do módulo aberto — ex. a ferramenta de posicionar do Table top. */
  gruposExtras?: Array<{ id: string; rotulo: string; ferramentas: Ferramenta[] }>
  /**
   * Põe os grupos do módulo no topo da trilha, antes de enquadramento e vistas.
   * É o que o Table top pede: ali o catálogo de objetos é o comando
   * principal do módulo, não um acessório da câmera.
   */
  gruposExtrasNoTopo?: boolean
}

/**
 * Trilha vertical de ferramentas universais do ambiente 3D.
 *
 * A regra que define o conteúdo desta barra é "não criar botões falsos": só
 * entra aqui comando que realmente faz alguma coisa. Por isso NÃO existem aqui
 * um botão "Selecionar" — a seleção está sempre ativa, não é um modo — nem uma
 * ferramenta de medição, que não existe no sistema e cuja invenção mexeria em
 * conteúdo didático (medir o quê, em que plano, com que precisão).
 *
 * Orbitar, deslocar e aproximar por gesto continuam no mouse e no toque, onde
 * sempre estiveram; a trilha oferece o que o gesto não dá — vistas nomeadas,
 * enquadramento e retorno ao padrão.
 *
 * Desfazer e refazer aparecem sempre, e ficam desabilitados com explicação nos
 * módulos que ainda não têm histórico. Esconder seria pior: o aluno não
 * descobriria que a operação existe em outro módulo.
 */
export function TrilhaFerramentas({
  camera,
  temSelecao,
  historico,
  motivoSemHistorico,
  modoLayout,
  gruposExtras,
  gruposExtrasNoTopo,
}: Props) {
  const compacta = modoLayout === 'tablet'

  const vistas: Array<{ tipo: VistaNomeada; rotulo: string; Icone: ComponentType<PropsIcone> }> = [
    { tipo: 'vistaFrontal', rotulo: 'Vista frontal', Icone: IconeVistaFrontal },
    { tipo: 'vistaLateral', rotulo: 'Vista lateral', Icone: IconeVistaLateral },
    { tipo: 'vistaSuperior', rotulo: 'Vista superior', Icone: IconeVistaSuperior },
    { tipo: 'vistaIsometrica', rotulo: 'Vista isométrica', Icone: IconeVistaIsometrica },
  ]

  const extras = gruposExtras ?? []

  const grupos: Array<{ id: string; rotulo: string; ferramentas: Ferramenta[] }> = [
    ...(gruposExtrasNoTopo ? extras : []),
    {
      id: 'enquadramento',
      rotulo: 'Enquadramento',
      ferramentas: [
        {
          id: 'aproximar',
          rotulo: 'Aproximar',
          Icone: IconeAproximar,
          aoAcionar: () => camera.executar('aproximar'),
        },
        {
          id: 'afastar',
          rotulo: 'Afastar',
          Icone: IconeAfastar,
          aoAcionar: () => camera.executar('afastar'),
        },
        {
          id: 'enquadrar',
          rotulo: 'Enquadrar seleção',
          Icone: IconeEnquadrarSelecao,
          aoAcionar: () => camera.executar('enquadrarSelecao'),
          motivoIndisponivel: temSelecao ? undefined : 'Selecione algo na cena para enquadrar',
        },
        {
          id: 'restaurar',
          rotulo: 'Restaurar enquadramento',
          Icone: IconeRestaurarCamera,
          aoAcionar: () => camera.executar('restaurar'),
        },
      ],
    },
    {
      id: 'vistas',
      rotulo: 'Vistas',
      ferramentas: vistas.map(({ tipo, rotulo, Icone }) => ({
        id: tipo,
        rotulo,
        Icone,
        aoAcionar: () => camera.executar(tipo),
        ativa: camera.vistaAtiva === tipo,
      })),
    },
    ...(gruposExtrasNoTopo ? [] : extras),
    {
      id: 'historico',
      rotulo: 'Histórico',
      ferramentas: [
        {
          id: 'desfazer',
          rotulo: 'Desfazer',
          Icone: IconeDesfazer,
          aoAcionar: () => historico?.desfazer(),
          motivoIndisponivel: !historico
            ? motivoSemHistorico ?? 'Este módulo não guarda histórico'
            : historico.podeDesfazer ? undefined : 'Nada para desfazer',
        },
        {
          id: 'refazer',
          rotulo: 'Refazer',
          Icone: IconeRefazer,
          aoAcionar: () => historico?.refazer(),
          motivoIndisponivel: !historico
            ? motivoSemHistorico ?? 'Este módulo não guarda histórico'
            : historico.podeRefazer ? undefined : 'Nada para refazer',
        },
      ],
    },
  ]

  return (
    <div
      className={`trilha-ferramentas${compacta ? ' trilha-ferramentas--compacta' : ''}`}
      role="toolbar"
      aria-orientation="vertical"
      aria-label="Ferramentas do ambiente 3D"
    >
      {grupos.map((grupo) => (
        <div key={grupo.id} className="trilha-ferramentas__grupo" role="group" aria-label={grupo.rotulo}>
          {grupo.ferramentas.map((ferramenta) => {
            const indisponivel = ferramenta.motivoIndisponivel
            const nomeComAtalho = ferramenta.atalho
              ? `${ferramenta.rotulo} (${ferramenta.atalho})`
              : ferramenta.rotulo
            return (
              <button
                key={ferramenta.id}
                type="button"
                className={`trilha-ferramentas__botao${ferramenta.ativa ? ' ativo' : ''}`}
                onClick={ferramenta.aoAcionar}
                disabled={indisponivel != null}
                aria-label={ferramenta.rotulo}
                aria-pressed={ferramenta.ativa}
                title={indisponivel ? `${nomeComAtalho}: ${indisponivel}` : nomeComAtalho}
              >
                <ferramenta.Icone tamanho={18} />
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}
